import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { PssSession, PssPlayerDetails, TournamentSummary, TournamentForecast, TournamentForecastBacktest } from '../../../services/pss-auth.service';
import { TournamentPanelComponent } from './tournament-panel/tournament-panel.component';


@Component({
  selector: 'app-tournament-tab',
  standalone: true,
  imports: [CommonModule, TournamentPanelComponent],
  templateUrl: './tournament-tab.component.html',
  styleUrls: ['./tournament-tab.component.css'],
})
export class TournamentTabComponent implements OnInit, OnDestroy {
  @Input() session: PssSession | null = null;
  @Input() details: PssPlayerDetails | null = null;

  private destroy$ = new Subject<void>();

  // ── Layout ─────────────────────────────────────────────────────────────────
  isFullScreen     = false;
  compareOpen      = false;
  /** 0 = primary panel, 1 = compare panel (matters on small screens) */
  activePanelIndex: 0 | 1 = 0;

  // ── Cross-panel comparison data ────────────────────────────────────────────
  primaryData: TournamentSummary | null = null;
  compareData: TournamentSummary | null = null;
  primaryForecastData: TournamentForecast | null = null;
  compareForecastData: TournamentForecast | null = null;
  compareInitialBacktest: TournamentForecastBacktest | null = null;

  // ── Favorites (shared across both panels) ──────────────────────────────────
  private static readonly TOURN_FLEET_FAVORITES_KEY = 'pss_tournament_fleet_favorites';
  tournFavoriteFleets: { allianceId: string; allianceName: string }[] = [];
  private tournFavoriteSet = new Set<string>();

  ngOnInit(): void {
    this.loadTournFavorites();
  }

  ngOnDestroy(): void {
    this.setCardWide(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Favorites management ───────────────────────────────────────────────────

  private loadTournFavorites(): void {
    try {
      const raw = localStorage.getItem(TournamentTabComponent.TOURN_FLEET_FAVORITES_KEY);
      const saved = raw ? JSON.parse(raw) : [];
      this.tournFavoriteFleets = Array.isArray(saved) ? saved.filter((f: any) => f && f.allianceId) : [];
    } catch {
      this.tournFavoriteFleets = [];
    }
    this.tournFavoriteSet = new Set(this.tournFavoriteFleets.map(f => f.allianceId));
  }

  private saveTournFavorites(): void {
    try {
      localStorage.setItem(TournamentTabComponent.TOURN_FLEET_FAVORITES_KEY, JSON.stringify(this.tournFavoriteFleets));
    } catch {}
  }

  onFavoriteToggled(event: { allianceId: string; allianceName: string; add: boolean }): void {
    if (event.add) {
      if (!this.tournFavoriteSet.has(event.allianceId)) {
        this.tournFavoriteFleets = [{ allianceId: event.allianceId, allianceName: event.allianceName }, ...this.tournFavoriteFleets];
        this.tournFavoriteSet.add(event.allianceId);
      }
    } else {
      this.tournFavoriteFleets = this.tournFavoriteFleets.filter(f => f.allianceId !== event.allianceId);
      this.tournFavoriteSet.delete(event.allianceId);
    }
    this.saveTournFavorites();
  }

  onPrimaryDataChanged(data: TournamentSummary | null): void { this.primaryData = data; }
  onCompareDataChanged(data: TournamentSummary | null): void { this.compareData = data; }
  onPrimaryForecastChanged(data: TournamentForecast | null): void { this.primaryForecastData = data; }
  onCompareForecastChanged(data: TournamentForecast | null): void { this.compareForecastData = data; }
  onPrimaryProjectionRequested(data: TournamentForecastBacktest): void {
    this.compareInitialBacktest = data;
    if (!this.compareOpen) {
      this.compareOpen = true;
      this.setCardWide(true);
    }
    this.activePanelIndex = 1;
  }

  // ── Layout controls ─────────────────────────────────────────────────────────

  toggleFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
    document.body.style.overflow = this.isFullScreen ? 'hidden' : '';
  }

  toggleCompare(): void {
    this.compareOpen = !this.compareOpen;
    this.setCardWide(this.compareOpen);
    if (!this.compareOpen) {
      this.activePanelIndex       = 0;
      this.compareData            = null;
      this.compareForecastData    = null;
      this.compareInitialBacktest = null;
    }
  }

  switchPanel(): void {
    this.activePanelIndex = this.activePanelIndex === 0 ? 1 : 0;
  }

  private setCardWide(wide: boolean): void {
    document.querySelector('.card')?.classList.toggle('card--wide', wide);
  }
}

